import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPreferences, savePreferences } from '../services/api';
import AlertBox from '../components/AlertBox';

const Preferences = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [alertMessage, setAlertMessage] = useState(null);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [user, setUser] = useState(null);

    const [formData, setFormData] = useState({
        breakfast_at: '',
        lunch_at: '',
        dinner_at: '',
        breakfast_medication: false,
        lunch_medication: false,
        dinner_medication: false,
        allow_play: false,
        play_from: '',
        play_to: ''
    });

    useEffect(() => {
        const userData = localStorage.getItem('user');
        if (!userData) {
            navigate('/login');
            return;
        }
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);

        // Fetch existing preferences
        // Assuming user has access to at least one room, we pick the first one or handle selection
        // For this POC, we just pick the first room from the list if available, or hardcode/ask user.
        // The login response gave `rooms_allowed`.

        const roomId = parsedUser.rooms === '*' ? '101' : parsedUser.rooms.split(',')[0]; // Fallback logic

        fetchPreferences(roomId);
    }, [navigate]);

    const fetchPreferences = async (roomId) => {
        try {
            const response = await getPreferences(roomId);
            if (response.data) {
                // Map API data to form state
                // API returns keys like BreakfastAt, we need to map to breakfast_at
                // Also handle datetime to time string conversion if needed
                const data = response.data;

                const formatTime = (dtString) => {
                    if (!dtString) return '';
                    // If it's a full ISO string, extract HH:MM
                    // If it's already HH:MM, leave it.
                    if (dtString.includes('T')) {
                        return dtString.split('T')[1].substring(0, 5);
                    }
                    return dtString.substring(0, 5);
                };

                setFormData({
                    breakfast_at: formatTime(data.BreakfastAt),
                    lunch_at: formatTime(data.LunchAt),
                    dinner_at: formatTime(data.DinnerAt),
                    breakfast_medication: data.BreakfastMedication === 1 || data.BreakfastMedication === true || false,
                    lunch_medication: data.LunchMedication === 1 || data.LunchMedication === true || false,
                    dinner_medication: data.DinnerMedication === 1 || data.DinnerMedication === true || false,
                    allow_play: data.AllowPlayingDuringDayWithOtherDogs === 1 || data.AllowPlayingDuringDayWithOtherDogs === true,
                    play_from: formatTime(data.PlayOutOfRoomFrom),
                    play_to: formatTime(data.PlayOutOfRoomTo)
                });
            }
        } catch (err) {
            console.error("Failed to load preferences", err);
            setAlertMessage('Failed to load preferences.');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setMessage({ type: '', text: '' });

        try {
            // Prepare data for API
            // We need to send full datetime strings or let backend handle it.
            // Let's assume backend expects "YYYY-MM-DD HH:MM:SS" or similar.
            // We will prepend a dummy date.
            const dummyDate = new Date().toISOString().split('T')[0];

            const formatForApi = (timeStr) => {
                if (!timeStr) return null;
                return `${dummyDate} ${timeStr}:00`;
            };

            const payload = {
                breakfast_at: formatForApi(formData.breakfast_at),
                lunch_at: formatForApi(formData.lunch_at),
                dinner_at: formatForApi(formData.dinner_at),
                breakfast_medication: formData.breakfast_medication,
                lunch_medication: formData.lunch_medication,
                dinner_medication: formData.dinner_medication,
                allow_play: formData.allow_play,
                play_from: formatForApi(formData.play_from),
                play_to: formatForApi(formData.play_to)
            };

            const roomId = user.rooms === '*' ? '101' : user.rooms.split(',')[0];

            await savePreferences(roomId, payload);
            setAlertMessage('Preferences saved successfully!');
        } catch (err) {
            setAlertMessage('Failed to save preferences.');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="container text-center mt-4">Loading...</div>;

    return (
        <div style={{ 
            width: '100%', 
            maxWidth: '480px', 
            margin: '0 auto',
            minHeight: '100vh',
            background: '#ffffff'
        }}>
            {/* Header */}
            <header style={{
                padding: '12px 16px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                background: '#f0f0f0',
                borderBottom: '1px solid #e0e0e0'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <img 
                        src="/dog_mascot.png" 
                        alt="Logo"
                        style={{ 
                            height: '32px',
                            width: 'auto',
                            objectFit: 'contain'
                        }}
                        onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'inline';
                        }}
                    />
                    <span style={{ fontSize: '24px', display: 'none' }}>🐕</span>
                    <h2 style={{ 
                        margin: 0, 
                        fontSize: '18px', 
                        fontWeight: 'bold', 
                        color: '#000',
                        fontFamily: 'Arial, sans-serif'
                    }}>
                        My Pup : Preferences
                    </h2>
                </div>
                <button 
                    onClick={() => navigate('/video-stream')} 
                    style={{ 
                        background: 'transparent', 
                        border: 'none', 
                        color: '#AE3041',
                        fontSize: '20px',
                        cursor: 'pointer',
                        padding: '4px',
                        fontFamily: 'Arial, sans-serif'
                    }}
                >
                    ⬆️
                </button>
            </header>

            <div style={{ padding: '16px' }}>

                {message.text && (
                    <div className={`mb-4 p-2 text-center`} style={{
                        backgroundColor: message.type === 'error' ? 'rgba(207, 102, 121, 0.1)' : 'rgba(3, 218, 198, 0.1)',
                        color: message.type === 'error' ? '#cf6679' : '#03dac6',
                        borderRadius: '4px',
                        marginBottom: '16px'
                    }}>
                        {message.text}
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <h3 className="mb-4" style={{ 
                        color: '#000', 
                        fontWeight: 'bold', 
                        marginBottom: '16px',
                        fontFamily: 'Arial, sans-serif',
                        fontSize: '16px'
                    }}>Meal times</h3>

                    <div className="mb-4" style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                        <span style={{ color: '#000', minWidth: '80px' }}>Breakfast</span>
                        <input
                            type="checkbox"
                            checked={!!formData.breakfast_at}
                            onChange={(e) => {
                                if (e.target.checked) {
                                    setFormData(prev => ({ ...prev, breakfast_at: prev.breakfast_at || '08:00' }));
                                } else {
                                    setFormData(prev => ({ ...prev, breakfast_at: '' }));
                                }
                            }}
                            style={{ width: '20px', height: '20px' }}
                        />
                        <>
                            <input
                                type="time"
                                id="breakfast_at"
                                name="breakfast_at"
                                value={formData.breakfast_at}
                                onChange={handleChange}
                                style={{
                                    padding: '8px',
                                    border: '1px solid #e0e0e0',
                                    borderRadius: '4px',
                                    background: '#ffffff'
                                }}
                            />
                        </>
                        <input
                            type="checkbox"
                            name="breakfast_medication"
                            id="breakfast_medication"
                            checked={formData.breakfast_medication}
                            onChange={handleChange}
                            style={{ width: '20px', height: '20px', marginLeft: '8px' }}
                        />
                        <label htmlFor="breakfast_medication" style={{ color: '#000', fontSize: '14px' }}>Provide medication</label>
                    </div>

                    <div className="mb-4" style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                        <span style={{ color: '#000', minWidth: '80px' }}>Lunch</span>
                        <input
                            type="checkbox"
                            checked={!!formData.lunch_at}
                            onChange={(e) => {
                                if (e.target.checked) {
                                    setFormData(prev => ({ ...prev, lunch_at: prev.lunch_at || '12:00' }));
                                } else {
                                    setFormData(prev => ({ ...prev, lunch_at: '' }));
                                }
                            }}
                            style={{ width: '20px', height: '20px' }}
                        />
                        <>
                            <input
                                type="time"
                                id="lunch_at"
                                name="lunch_at"
                                value={formData.lunch_at}
                                onChange={handleChange}
                                style={{
                                    padding: '8px',
                                    border: '1px solid #e0e0e0',
                                    borderRadius: '4px',
                                    background: '#ffffff'
                                }}
                            />
                        </>
                        <input
                            type="checkbox"
                            name="lunch_medication"
                            id="lunch_medication"
                            checked={formData.lunch_medication}
                            onChange={handleChange}
                            style={{ width: '20px', height: '20px', marginLeft: '8px' }}
                        />
                        <label htmlFor="lunch_medication" style={{ color: '#000', fontSize: '14px' }}>Provide medication</label>
                    </div>

                    <div className="mb-4" style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                        <span style={{ color: '#000', minWidth: '80px' }}>Dinner</span>
                        <input
                            type="checkbox"
                            checked={!!formData.dinner_at}
                            onChange={(e) => {
                                if (e.target.checked) {
                                    setFormData(prev => ({ ...prev, dinner_at: prev.dinner_at || '18:00' }));
                                } else {
                                    setFormData(prev => ({ ...prev, dinner_at: '' }));
                                }
                            }}
                            style={{ width: '20px', height: '20px' }}
                        />
                        <>
                            <input
                                type="time"
                                id="dinner_at"
                                name="dinner_at"
                                value={formData.dinner_at}
                                onChange={handleChange}
                                style={{
                                    padding: '8px',
                                    border: '1px solid #e0e0e0',
                                    borderRadius: '4px',
                                    background: '#ffffff'
                                }}
                            />
                        </>
                        <input
                            type="checkbox"
                            name="dinner_medication"
                            id="dinner_medication"
                            checked={formData.dinner_medication}
                            onChange={handleChange}
                            style={{ width: '20px', height: '20px', marginLeft: '8px' }}
                        />
                        <label htmlFor="dinner_medication" style={{ color: '#000', fontSize: '14px' }}>Provide medication</label>
                    </div>

                    <h3 className="mb-4 mt-4" style={{ color: '#000', fontWeight: 'bold', marginBottom: '16px', marginTop: '24px' }}>Play times</h3>

                    <div className="mb-4" style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                        <input
                            type="checkbox"
                            name="allow_play"
                            id="allow_play"
                            checked={formData.allow_play}
                            onChange={handleChange}
                            style={{ width: '20px', height: '20px' }}
                        />
                        <label htmlFor="allow_play" style={{ color: '#000' }}>Allow playing with daycare dogs during the day</label>
                    </div>

                    <div className="mb-4" style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                        <input
                            type="checkbox"
                            name="allow_outdoor"
                            id="allow_outdoor"
                            checked={!!formData.play_from}
                            onChange={(e) => {
                                if (!e.target.checked) {
                                    setFormData(prev => ({ ...prev, play_from: '', play_to: '' }));
                                } else {
                                    setFormData(prev => ({ ...prev, play_from: prev.play_from || '08:00', play_to: prev.play_to || '18:00' }));
                                }
                            }}
                            style={{ width: '20px', height: '20px' }}
                        />
                        <label htmlFor="allow_outdoor" style={{ color: '#000' }}>Allow playing out of the room</label>
                    </div>

                    <div className="mb-4" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '24px', marginLeft: '32px' }}>
                        <input
                            type="time"
                            name="play_from"
                            value={formData.play_from}
                            onChange={handleChange}
                            style={{
                                padding: '8px',
                                border: '1px solid #e0e0e0',
                                borderRadius: '4px',
                                background: '#ffffff'
                            }}
                        />
                        <span style={{ color: '#000' }}>to</span>
                        <input
                            type="time"
                            name="play_to"
                            value={formData.play_to}
                            onChange={handleChange}
                            style={{
                                padding: '8px',
                                border: '1px solid #e0e0e0',
                                borderRadius: '4px',
                                background: '#ffffff'
                            }}
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={saving}
                        style={{
                            width: '100%',
                            padding: '14px',
                            background: '#AE3041',
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            cursor: saving ? 'not-allowed' : 'pointer',
                            marginTop: '24px',
                            fontFamily: 'Arial, sans-serif'
                        }}
                    >
                        {saving ? 'Saving...' : 'Save'}
                    </button>
                </form>
                {/** modals */}
                <AlertBox
                    show = {alertMessage != null}
                    title="Alert"
                    message={alertMessage}
                    onOK= {()=>setAlertMessage(null)}/>
            </div>
        </div>
    );
};

export default Preferences;
